import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {type ScribeArtifact, useDeleteScribeArtifact} from '@github-ui/scribe-artifacts'
import {ChevronDownIcon, ChevronRightIcon, FileIcon, LinkIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, CounterLabel, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {TEST_IDS} from '../../constants/test-ids'
import type {PanelArtifact} from './ArtifactPanelContent'
import {PersistedArtifactRow} from './ArtifactRow'
import styles from './ArtifactsSection.module.css'
import {ArtifactsSidePanelOverlay} from './ArtifactsSidePanelOverlay'
import {groupArtifactsByType} from './group-artifacts'
import {shouldShowArtifactsSection} from './issue-artifacts-visibility'
import type {IssueArtifact} from './types'
import {useArtifactsPanelState} from './use-artifacts-panel-state'
import {useCreateArtifactDialog} from './use-create-artifact-dialog'

/** Maps a Scribe API artifact (snake_case payload) onto the section's view model. */
function mapScribeArtifact(artifact: ScribeArtifact, index: number): IssueArtifact {
  const numericId = typeof artifact.id === 'number' ? artifact.id : Number(artifact.id)
  return {
    id: Number.isFinite(numericId) ? numericId : index + 1,
    number: artifact.number ?? index + 1,
    type: artifact.type === 'plan' || artifact.type === 'link' ? artifact.type : 'generic',
    name: artifact.name,
    description: artifact.description ?? undefined,
    body: artifact.body ?? '',
    bodyHTML: artifact.body_html ?? undefined,
    actor: {login: artifact.creator?.login ?? '', avatarUrl: ''},
    createdAt: artifact.created_at ?? '',
    updatedAt: artifact.updated_at ?? '',
  }
}

type ArtifactsSectionContentProps = {
  resourceId: number
  artifacts: ScribeArtifact[]
  onSelectArtifact: (artifact: PanelArtifact) => void
  onArtifactDelete?: (id: number) => void
  onLastArtifactDeleted?: () => void
}

function ArtifactsSectionContent({
  resourceId,
  artifacts,
  onSelectArtifact,
  onArtifactDelete,
  onLastArtifactDeleted,
}: ArtifactsSectionContentProps) {
  const persistedArtifacts = useMemo<IssueArtifact[]>(
    () => groupArtifactsByType(artifacts.map(mapScribeArtifact)),
    [artifacts],
  )

  const {mutate: deleteArtifact} = useDeleteScribeArtifact({resourceType: 'issue', resourceId})

  const artifactNumbersById = useMemo(
    () => new Map(persistedArtifacts.map(artifact => [artifact.id, artifact.number])),
    [persistedArtifacts],
  )

  const totalArtifacts = persistedArtifacts.length

  const handleDelete = useCallback(
    (id: number) => {
      const number = artifactNumbersById.get(id)
      if (number === undefined) return
      deleteArtifact(
        {number},
        {
          onSuccess: () => {
            onArtifactDelete?.(id)
            if (totalArtifacts === 1) {
              onLastArtifactDeleted?.()
            }
          },
        },
      )
    },
    [artifactNumbersById, deleteArtifact, onArtifactDelete, onLastArtifactDeleted, totalArtifacts],
  )

  if (totalArtifacts === 0) {
    return null
  }

  return (
    <ul className={styles.List} aria-label="Artifacts">
      {persistedArtifacts.map(artifact => (
        <PersistedArtifactRow
          key={`${artifact.type}:${artifact.id}`}
          id={artifact.id}
          type={artifact.type}
          name={artifact.name}
          description={artifact.description}
          body={artifact.body}
          bodyHTML={artifact.bodyHTML}
          onSelect={onSelectArtifact}
          onDelete={handleDelete}
        />
      ))}
    </ul>
  )
}

type ArtifactsSectionProps = {
  resourceId: number
  artifacts: ScribeArtifact[]
  focusOnMount?: boolean
  onArtifactDelete?: (id: number) => void
  onFocusTargetReady?: () => void
  onLastArtifactDeleted?: () => void
}

export function ArtifactsSection({
  resourceId,
  artifacts,
  focusOnMount = false,
  onArtifactDelete,
  onFocusTargetReady,
  onLastArtifactDeleted,
}: ArtifactsSectionProps) {
  const [isExpanded, setIsExpanded] = useState<boolean | null>(null)
  const toggleButtonRef = useRef<HTMLButtonElement>(null)

  const {
    activeArtifactId,
    activeArtifact,
    tabs,
    lastRowTriggerRef,
    selectArtifact: handleSelectArtifact,
    activate: handleActivate,
    closePanel: handleClosePanel,
    promoteOverflow: handlePromoteOverflow,
    closeTab: handleCloseTab,
  } = useArtifactsPanelState()

  const artifactCount = artifacts.length
  // Default expanded when artifacts exist, collapsed when empty. User toggle wins.
  const effectiveExpanded = isExpanded ?? artifactCount > 0

  useEffect(() => {
    if (!focusOnMount) return
    const toggleButton = toggleButtonRef.current
    if (!toggleButton) return
    toggleButton.focus()
    onFocusTargetReady?.()
  }, [focusOnMount, onFocusTargetReady])

  const toggleExpanded = useCallback(() => {
    setIsExpanded(expanded => !(expanded ?? artifactCount > 0))
  }, [artifactCount])

  const handleArtifactCreated = useCallback(() => {
    setIsExpanded(true)
  }, [])

  const {
    createArtifactDialog,
    openCreateArtifactDialog: handleCreateArtifact,
    openCreateLinkDialog: handleCreateLink,
    triggerRef: addArtifactButtonRef,
  } = useCreateArtifactDialog({resourceId, onCreated: handleArtifactCreated})

  if (!shouldShowArtifactsSection(artifacts)) return null

  return (
    <div
      className={clsx(styles.Container, isFeatureEnabled('issue_inline_avatars') && styles.inlineAvatars)}
      data-testid={TEST_IDS.issueArtifactsSection}
    >
      <div className={styles.Header}>
        <div className={styles.TitleGroup}>
          <IconButton
            ref={toggleButtonRef}
            icon={effectiveExpanded ? ChevronDownIcon : ChevronRightIcon}
            variant="invisible"
            size="small"
            aria-label={effectiveExpanded ? 'Collapse artifacts' : 'Expand artifacts'}
            aria-expanded={effectiveExpanded}
            onClick={toggleExpanded}
          />
          <span className={styles.TitleLabel}>Artifacts</span>
          {artifactCount > 0 && <CounterLabel>{artifactCount}</CounterLabel>}
        </div>
        <ActionMenu anchorRef={addArtifactButtonRef}>
          <ActionMenu.Anchor>
            <IconButton
              icon={PlusIcon}
              variant="invisible"
              size="small"
              aria-label="Add artifact"
              disabled={resourceId <= 0}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.Item onSelect={handleCreateLink}>
                <ActionList.LeadingVisual>
                  <LinkIcon />
                </ActionList.LeadingVisual>
                Add link
              </ActionList.Item>
              <ActionList.Divider />
              <ActionList.Item onSelect={handleCreateArtifact}>
                <ActionList.LeadingVisual>
                  <FileIcon />
                </ActionList.LeadingVisual>
                Create artifact
              </ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>

      <div
        className={clsx(styles.Body, !effectiveExpanded && styles.collapsed)}
        inert={!effectiveExpanded || undefined}
      >
        <div className={styles.BodyInner}>
          <ErrorBoundary fallback={null} critical={false} boundaryName="ArtifactsSection">
            <ArtifactsSectionContent
              resourceId={resourceId}
              artifacts={artifacts}
              onSelectArtifact={handleSelectArtifact}
              onArtifactDelete={onArtifactDelete}
              onLastArtifactDeleted={onLastArtifactDeleted}
            />
          </ErrorBoundary>
        </div>
      </div>

      {activeArtifactId !== null && (
        <ArtifactsSidePanelOverlay
          open
          onClose={handleClosePanel}
          tabs={tabs}
          activeId={activeArtifactId}
          activeArtifact={activeArtifact}
          onActivate={handleActivate}
          onPromoteOverflowTab={handlePromoteOverflow}
          onCloseTab={handleCloseTab}
          returnFocusRef={lastRowTriggerRef}
        />
      )}
      {createArtifactDialog}
    </div>
  )
}
